import { openDrawer, closeDrawer } from "@/src/components/dialogs";
import { MyDynamicSelect } from "@/src/components/dynamicSelect";
import { Input } from "@/src/components/universalInput";
import { UploadSpots } from "@/src/components/uploadSpots";
import {
  Appointment,
  Patient,
  User,
  AppointmentType,
} from "@/src/data/allTypes";
import { USER } from "@/src/data/globalVariables";
import apiFetch from "@/src/utils/apiFetch";
import { isCabinet, uid, getItemId, getServiceId } from "@/src/utils/utils";
import { useState } from "react";
import { openAppointmentDemandForm } from "./appointmentsUtils";
import AppointmentTypeCard from "./AppointmentTypeCard";

export async function openAppointmentFrom({
  appointment,
  onConfirm,
  patient,
  selectedUserId,
}: {
  appointment: Appointment;
  patient: Patient;
  selectedUserId: string | null | undefined;
  onConfirm: (appointment: Appointment) => void;
}) {
  await openDrawer({
    id: "appointmentDrawer",
    title: "Ajouter un rendez-vous",
    content: (
      <AppointmentForm
        user={selectedUserId as any}
        appointmentTypes={USER.get()?.canScheduleTypes || []}
        patient={patient!}
        current={appointment}
        onChange={(newValue) => {
          appointment = newValue;
        }}
      />
    ),
    onConfirm: async () => {
      if (!appointment.patient) return;
      if (!appointment.type) return;
      appointment = await openAppointmentDemandForm(appointment);
      let result = await apiFetch("appointments", "addOne", {
        appointment,
      });
      if (!result) return;
      console.log("appointment result", result);
      onConfirm(result);
      closeDrawer("patientSelectDrawer");
    },
  });
}

export function AppointmentForm({
  user,
  patient,
  appointmentTypes,
  current,
  onChange,
}: {
  user: User | null | undefined;
  patient: Patient | null | undefined;
  current?: Appointment;
  appointmentTypes?: (AppointmentType | null | undefined)[] | null | undefined;
  onChange: (newValue: Appointment) => void;
}) {
  let [appointment, setAppointment] = useState(current || ({} as Appointment));
  appointment.type ||=
    appointmentTypes?.[0] || USER.get()?.service?.appointmentTypes?.[0];
  function setNewValue(newValue: Appointment) {
    setAppointment(newValue);
    onChange(newValue);
  }

  function getAppointmentTypes() {
    if (isCabinet()) return USER.get()?.service?.appointmentTypes;
    if (appointmentTypes) return appointmentTypes;
    return USER.get()?.service?.appointmentTypes;
  }

  const length = getAppointmentTypes()?.length || 0;

  return (
    <div className="grid c1 gap20">
      <div className="field">
        <div className="label">Type de rendez-vous</div>
        {length == 0 && (
          <div className="gray p20 tcenter">
            Vous n&apos;avez pas les autorisations nécessaires pour choisir un
            type de rendez-vous. <br /> <br /> Veuillez contacter
            l&apos;administrateur
          </div>
        )}
        <div className={`grid ${length == 1 ? "c1" : "c2"} gap10`}>
          {getAppointmentTypes()?.map((type) => {
            if (!type) return null;
            let isSelected = appointment.type?.id == type.id;
            return (
              <AppointmentTypeCard
                key={uid()}
                isActive={isSelected}
                type={type}
                onClick={() => {
                  setNewValue({ ...appointment, type });
                }}
              ></AppointmentTypeCard>
            );
          })}
        </div>
      </div>

      <UploadSpots
        name="Pièces jointes"
        value={appointment.uploads}
        gridCount={2}
        spotsCount={2}
        additionalUploadData={{
          patient: getItemId(patient),
        }}
        onChange={(newValue) => {
          setNewValue({
            ...appointment,
            uploads: newValue,
          });
        }}
      ></UploadSpots>

      {(USER.get()?.role?.name?.includes("reception") ||
        USER.get()?.role?.name?.includes("accueil")) && (
        <MyDynamicSelect
          queryFn={apiFetch("users", "loadMany", {
            serviceId: getServiceId(),
          })}
          queryKey={["calendarLoadUsers", getServiceId()]}
          valueContructor={(user) => user.id}
          nameConstructor={(user) => `${user.firstName} ${user.lastName}`}
          placeholder={"Choisir un agenda"}
          name={"Agenda"}
          value={getItemId(appointment?.doctor)}
          onChange={(newId, newValue) => {
            setNewValue({ ...appointment, doctor: newValue });
          }}
        />
      )}

      {!isCabinet() && (
        <Input
          name={"Urgent ?"}
          value={appointment.isUrgent == true ? "true" : "false"}
          type="chips"
          options={[
            { label: "Oui", value: "true" },
            { label: "Non", value: "false" },
          ]}
          onChange={(newValue) => {
            setNewValue({ ...appointment, isUrgent: newValue == "true" });
          }}
        ></Input>
      )}

      {!isCabinet() && (
        <Input
          name={"Hospitalisé ?"}
          value={appointment.isHospitalized == true ? "true" : "false"}
          type="chips"
          options={[
            { label: "Oui", value: "true" },
            { label: "Non", value: "false" },
          ]}
          onChange={(newValue) => {
            setNewValue({ ...appointment, isHospitalized: newValue == "true" });
          }}
        ></Input>
      )}

      <Input
        name={"Notes"}
        value={appointment.notes}
        type="textarea"
        onChange={(newValue) => {
          setNewValue({ ...appointment, notes: newValue });
        }}
      ></Input>
    </div>
  );
}
